import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import AppLayout from '../components/AppLayout';
import { ClaimHealthCard, ClaimHealthBadge } from '../components/ClaimHealthTrafficLight';
import PolicyUpload from '../components/PolicyUpload';
import ComparativeAnalysis from '../components/ComparativeAnalysis';
import AdjusterQABot from '../components/AdjusterQABot';
import ScenarioProjections from '../components/ScenarioProjections';
import SubrogationPanel from '../components/SubrogationPanel';
import CollaborationPanel from '../components/CollaborationPanel';
import AIActivityPanel from '../components/AIActivityPanel';
import FutureClosurePanel from '../components/FutureClosurePanel';
import ShareReportModal from '../components/ShareReportModal';
import FairSettlementSection from '../components/accountant/FairSettlementSection';
import EmailNotificationSection from '../components/reports/EmailNotificationSection';
import CaseIntelligenceChat, { IntelligenceChatButton } from '../components/CaseIntelligenceChat';
import ParsedPLDisplay from '../components/ParsedPLDisplay';
import CaseReadinessPanel from '../components/CaseReadinessPanel';
import ExtractedDocumentsPanel from '../components/ExtractedDocumentsPanel';
import ExpenseCategoryEditor from '../components/ExpenseCategoryEditor';
import { CaseStrengthIndicator, ActionItemsList } from '../components/automation';
import { ArrowLeft, FileText, FileSpreadsheet } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';
import { useActivityLog } from '../hooks/useActivityLog';
import {
  ReportHeader,
  ClaimOverviewCard,
  BIResultsCard,
  SensitivityAnalysisCard,
  MonteCarloCard,
  DownloadOptionsCard,
} from '../components/report';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Report {
  id: string;
  client_name: string;
  claimant_name: string;
  business_address: string;
  business_state?: string;
  status: string;
  created_at: string;
  completed_at?: string;
  pdf_url?: string;
  pdf_s3_key?: string;
  fema_declaration_number?: string;
  event_type?: string;
  event_start_date?: string;
  event_end_date?: string;
  zone?: string;
  narrative?: {
    narrative_sections?: any;
    estimated_pages?: number;
    word_count?: number;
  };
  narrative_status?: string;
  sensitivity_analysis?: {
    parameter: string;
    results: {
      base_claim: number;
      range: {
        min: number;
        max: number;
        spread: number;
        spread_pct?: number;
      };
      results: Array<{ pct: number; claim: number }>;
    };
    run_at: string;
  };
  monte_carlo_analysis?: {
    iterations: number;
    results: {
      base_claim: number;
      results: {
        mean: number;
        median: number;
        std_dev: number;
        ci_95_low: number;
        ci_95_high: number;
        min: number;
        max: number;
      };
    };
    run_at: string;
  };
  result?: {
    final_claim_amount: number;
    revenue_loss: number;
    causation_passed: boolean;
    step1_variable_profit_loss?: number;
    step2_incremental_loss?: number;
    causation_test_used?: string;
    causation_failure_reason?: string;
  };
  financial_data?: any;
  coverage_year?: number;
  year_window?: number[];
  // P&L Bypass fields (Phase 1.5)
  has_pl_statement?: boolean;
  parsed_pl_statement?: any;
  pl_parsed_at?: string;
}

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Role-scoped rendering: DS sees data intake only, Accountant/Admin sees everything
  const viewRole = useMemo(() => {
    const paramRole = searchParams.get('role');
    if (paramRole === 'data_specialist') return 'data_specialist';
    if (paramRole === 'accountant') return 'accountant';
    // Check localStorage for role context (set by role dashboards)
    const stored = localStorage.getItem('claimsync_view_role');
    if (stored === 'data_specialist') return 'data_specialist';
    return 'admin'; // Default: full access
  }, [searchParams]);
  const showAnalytical = viewRole !== 'data_specialist';
  
  // Statistical analysis states
  const [sensitivityLoading, setSensitivityLoading] = useState(false);
  const [sensitivityParam, setSensitivityParam] = useState('payroll');
  const [monteCarloLoading, setMonteCarloLoading] = useState(false);
  const [autoAnalysisRunning, setAutoAnalysisRunning] = useState(false);
  const [autoRunTriggered, setAutoRunTriggered] = useState(false);
  
  // Health score state
  const [healthScore, setHealthScore] = useState<any>(null);
  const [healthScoreLoading, setHealthScoreLoading] = useState(false);
  
  // FEMA declaration state
  const [femaNumber, setFemaNumber] = useState('');
  const [femaEditing, setFemaEditing] = useState(false);
  const [femaSaving, setFemaSaving] = useState(false);
  const [femaError, setFemaError] = useState<string | null>(null);
  const [femaSuggestions, setFemaSuggestions] = useState<any[]>([]);
  const [femaLoading, setFemaLoading] = useState(false);
  const [femaState, setFemaState] = useState('');
  
  // Policy upload state
  const [showPolicyUpload, setShowPolicyUpload] = useState(false);
  const [policyData, setPolicyData] = useState<any>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Case Intelligence Chat state
  const [showIntelligenceChat, setShowIntelligenceChat] = useState(false);

  // Case Evaluation state (90% Automation)
  const [caseEvaluation, setCaseEvaluation] = useState<{
    case_strength: number;
    is_ready: boolean;
    status: string;
    blocking_count: number;
  } | null>(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);

  // US States for FEMA lookup
  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR', 'VI'
  ];

  // Fetch FEMA suggestions based on state selection
  const fetchFemaSuggestionsForState = async (state: string) => {
    if (!state || !report?.event_start_date) return;
    
    setFemaLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/v1/fema/suggest`, {
        state: state,
        event_start: report.event_start_date,
        event_end: report.event_end_date,
        event_type: report.event_type
      });
      setFemaSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch FEMA suggestions:', err);
    } finally {
      setFemaLoading(false);
    }
  };

  // Fetch FEMA suggestions based on report data (auto-detect state)
  const fetchFemaSuggestions = async () => {
    if (!report?.zone || !report?.event_start_date) return;
    
    setFemaLoading(true);
    try {
      // Extract state from zone (e.g., "Zone 1, NC" -> "NC")
      const zoneMatch = report.zone?.match(/([A-Z]{2})$/);
      const state = zoneMatch ? zoneMatch[1] : report.zone?.split(',').pop()?.trim();
      
      if (!state || state.length !== 2) {
        console.log('Could not extract state from zone:', report.zone);
        return;
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/v1/fema/suggest`, {
        state: state,
        event_start: report.event_start_date,
        event_end: report.event_end_date,
        event_type: report.event_type
      });
      
      setFemaSuggestions(response.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch FEMA suggestions:', err);
    } finally {
      setFemaLoading(false);
    }
  };

  // Save FEMA declaration number
  const saveFemaNumber = async () => {
    if (!id || !femaNumber.trim()) return;
    setFemaSaving(true);
    try {
      const token = await getToken();
      await axios.patch(
        `${BACKEND_URL}/api/v1/reports/${id}`,
        { fema_declaration_number: femaNumber.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFemaEditing(false);
      // Refresh health score
      const hsResponse = await axios.get(
        `${BACKEND_URL}/api/v1/claims/health-score/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHealthScore(hsResponse.data);
    } catch (err) {
      console.error('Failed to save FEMA number:', err);
      setFemaError('Failed to save FEMA declaration number');
    } finally {
      setFemaSaving(false);
    }
  };

  // Run Sensitivity Analysis
  const runSensitivityAnalysis = async (param?: string) => {
    if (!id) return;
    setSensitivityLoading(true);
    const paramToUse = param || sensitivityParam;
    try {
      const token = await getToken();
      await axios.post(
        `${BACKEND_URL}/api/v1/reports/${id}/sensitivity?parameter=${paramToUse}&min_pct=80&max_pct=120&step_pct=5`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh report to show new results
      const reportResponse = await axios.get(
        `${BACKEND_URL}/api/v1/reports/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(reportResponse.data);
    } catch (err: any) {
      console.error('Sensitivity analysis failed:', err);
    } finally {
      setSensitivityLoading(false);
    }
  };

  // Run Monte Carlo Simulation
  const runMonteCarloAnalysis = async () => {
    if (!id) return;
    setMonteCarloLoading(true);
    try {
      const token = await getToken();
      await axios.post(
        `${BACKEND_URL}/api/v1/reports/${id}/monte-carlo?iterations=1000&noise_pct=0.15&seed=42`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh report to show new results
      const reportResponse = await axios.get(
        `${BACKEND_URL}/api/v1/reports/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(reportResponse.data);
    } catch (err: any) {
      console.error('Monte Carlo failed:', err);
    } finally {
      setMonteCarloLoading(false);
    }
  };

  // Auto-run analyses when report is completed
  const autoRunAnalyses = async (reportData: Report) => {
    if (autoRunTriggered) return;
    if (reportData.status !== 'completed') return;
    if (!reportData.result) return;
    
    const needsSensitivity = !reportData.sensitivity_analysis;
    const needsMonteCarlo = !reportData.monte_carlo_analysis;
    
    if (!needsSensitivity && !needsMonteCarlo) return;
    
    setAutoRunTriggered(true);
    setAutoAnalysisRunning(true);
    const token = await getToken();
    
    // Auto-run Sensitivity Analysis if not already run
    if (needsSensitivity) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/v1/reports/${id}/sensitivity?parameter=payroll&min_pct=80&max_pct=120&step_pct=5`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Auto-run sensitivity failed:', err);
      }
    }
    
    // Auto-run Monte Carlo if not already run
    if (needsMonteCarlo) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/v1/reports/${id}/monte-carlo?iterations=1000&noise_pct=0.15&seed=42`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Auto-run monte carlo failed:', err);
      }
    }
    
    // Refresh report
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/reports/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(response.data);
    } catch (err) {
      console.error('Failed to refresh report:', err);
    }
    
    setAutoAnalysisRunning(false);
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(
          `${BACKEND_URL}/api/v1/reports/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const reportData = response.data;
        setReport(reportData);
        setFemaNumber(reportData.fema_declaration_number || '');
        setLoading(false);
        
        // Log view activity
        logActivity({
          reportId: id || '',
          actionType: 'view',
          actionDetails: `Viewed report for ${reportData.client_name}`,
          section: 'report_detail',
          metadata: { client_name: reportData.client_name, status: reportData.status }
        });
        
        // Fetch health score for completed reports
        if (reportData.status === 'completed') {
          try {
            setHealthScoreLoading(true);
            const hsResponse = await axios.get(
              `${BACKEND_URL}/api/v1/claims/health-score/${id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setHealthScore(hsResponse.data);
          } catch (hsErr) {
            console.error('Failed to fetch health score:', hsErr);
          } finally {
            setHealthScoreLoading(false);
          }
          
          // Auto-fetch FEMA suggestions if no FEMA number set
          if (!reportData.fema_declaration_number && reportData.event_start_date) {
            try {
              setFemaLoading(true);
              
              // Get state from business_state field or try to extract from zone/address
              let state = reportData.business_state;
              
              if (!state) {
                // Try to extract from zone
                const zoneStr = reportData.zone || '';
                const stateMatch = zoneStr.match(/([A-Z]{2})$/);
                state = stateMatch ? stateMatch[1] : null;
              }
              
              if (!state && reportData.business_address) {
                // Try to extract from address (e.g., "123 Main St, Atlanta, GA 30301")
                const addrMatch = reportData.business_address.match(/\b([A-Z]{2})\s*\d{5}/);
                state = addrMatch ? addrMatch[1] : null;
              }
              
              if (state) {
                setFemaState(state);
                console.log('FEMA Search - State:', state, 'Event:', reportData.event_type, 'Dates:', reportData.event_start_date, '-', reportData.event_end_date);
                const femaResponse = await axios.post(`${BACKEND_URL}/api/v1/fema/suggest`, {
                  state: state,
                  event_start: reportData.event_start_date,
                  event_end: reportData.event_end_date,
                  event_type: reportData.event_type
                });
                console.log('FEMA Response:', femaResponse.data);
                setFemaSuggestions(femaResponse.data.suggestions || []);
              } else {
                console.log('FEMA Search - No state found');
              }
            } catch (femaErr) {
              console.error('Failed to fetch FEMA suggestions:', femaErr);
            } finally {
              setFemaLoading(false);
            }
          }
          
          // Fetch case evaluation (90% Automation)
          try {
            setEvaluationLoading(true);
            const evalResponse = await axios.get(
              `${BACKEND_URL}/api/v1/attorney-ai/${id}/evaluation`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setCaseEvaluation({
              case_strength: evalResponse.data.case_strength || 0,
              is_ready: evalResponse.data.is_ready || false,
              status: evalResponse.data.status || 'not_ready',
              blocking_count: evalResponse.data.blocking_count || 0
            });
          } catch (evalErr) {
            console.debug('No case evaluation found:', evalErr);
            // Set defaults if no evaluation exists yet
            setCaseEvaluation(null);
          } finally {
            setEvaluationLoading(false);
          }
        }
        
        // Auto-run analyses for completed reports
        if (reportData.status === 'completed' && reportData.result) {
          autoRunAnalyses(reportData);
        }
      } catch (err: any) {
        console.error('Failed to fetch report:', err);
        setError(err.response?.data?.detail || 'Failed to load report');
        setLoading(false);
      }
    };

    fetchReport();
    
    // Poll every 3 seconds if report is still processing
    const interval = setInterval(() => {
      if (report?.status === 'processing' || report?.status === 'pending') {
        fetchReport();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [id, getToken, report?.status]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading report...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !report) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-red-600 mb-4">{error || 'Report not found'}</p>
            <Link to="/" className="text-blue-600 hover:underline flex items-center gap-2 justify-center">
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Helper function to calculate financial data summary
  const getFinancialDataSummary = () => {
    if (!report.financial_data) return null;
    
    const years: { [key: string]: { revenue: number; months: number } } = {};
    
    Object.keys(report.financial_data).forEach(key => {
      if (key.startsWith('data_')) {
        const year = key.replace('data_', '');
        const monthlyData = report.financial_data[key];
        if (Array.isArray(monthlyData)) {
          const totalRevenue = monthlyData.reduce((sum, month) => sum + (month.revenue || 0), 0);
          years[year] = {
            revenue: totalRevenue,
            months: monthlyData.length
          };
        }
      }
    });
    
    return Object.keys(years).length > 0 ? years : null;
  };

  const financialSummary = getFinancialDataSummary();

  return (
    <AppLayout>
      <div className="p-6">
        {/* Page Header - Using extracted component */}
        <ReportHeader 
          report={report} 
          onShareClick={() => setShowShareModal(true)} 
        />

        {/* Main Content Grid */}
        <div className="space-y-6">
          {/* Overview Card - Using extracted component */}
          <ClaimOverviewCard
            createdAt={report.created_at}
            completedAt={report.completed_at}
            eventType={report.event_type}
            coverageYear={report.coverage_year}
          />

          {/* PARSED P&L STATEMENT SECTION (Phase 1.5) */}
          {report.has_pl_statement && report.parsed_pl_statement && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <FileSpreadsheet className="w-5 h-5 mr-2 text-blue-600" />
                Parsed P&L Statement
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                  Direct Upload - No Reconstruction Needed
                </span>
              </h2>
              <ParsedPLDisplay data={report.parsed_pl_statement} showDetails={false} />
            </div>
          )}

          {/* CASE READINESS PANEL (P0) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Case Readiness Status</h2>
            <CaseReadinessPanel caseId={report.id} compact={false} showRefresh={true} />
          </div>

          {/* CASE STRENGTH & ACTION ITEMS (90% Automation Visibility) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Case Strength Indicator */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Case Strength
              </h2>
              {evaluationLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-20 bg-gray-100 rounded-lg" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                </div>
              ) : caseEvaluation ? (
                <CaseStrengthIndicator
                  score={caseEvaluation.case_strength}
                  isReady={caseEvaluation.is_ready}
                  isAutoReady={caseEvaluation.status === 'auto_ready'}
                  blocking={caseEvaluation.blocking_count}
                  showDetails={true}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-sm">No AI evaluation yet</p>
                  <p className="text-xs text-gray-400 mt-1">Evaluation runs after document processing</p>
                </div>
              )}
            </div>

            {/* Action Items List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Pending Action Items
              </h2>
              <ActionItemsList 
                reportId={report.id}
                compact={false}
                maxItems={5}
              />
            </div>
          </div>

          {/* EXTRACTED SUPPORTING DOCUMENTS (P2) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <ExtractedDocumentsPanel caseId={report.id} />
          </div>

          {/* EXPENSE CATEGORY EDITOR (P2: P&L Edit UI) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <ExpenseCategoryEditor 
              caseId={report.id}
              onSave={() => console.log('Expense corrections saved')}
              onRecalculate={() => window.location.reload()}
            />
          </div>

          {/* Causation Failed Banner - Show prominently with financial data summary */}
          {report.status === 'causation_failed' && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-amber-900 mb-2">Report Needs Attention</h3>
                  <p className="text-amber-800 mb-4">
                    {report.result?.causation_failure_reason || 'The causation test did not pass with the current data. This usually means the financial data does not show sufficient variance between the benchmark period and the event period to support a valid claim.'}
                  </p>
                  
                  {/* Show financial data summary */}
                  {financialSummary && (
                    <div className="bg-white rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Your Uploaded Financial Data:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.keys(financialSummary).sort().map(year => (
                          <div key={year} className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Year {year}</p>
                            <p className="text-sm font-bold text-gray-900">
                              {formatCurrency(financialSummary[year].revenue)}
                            </p>
                            <p className="text-xs text-gray-500">{financialSummary[year].months} months</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-amber-100 rounded-lg p-4">
                    <p className="font-semibold text-amber-900 mb-2">What you can do:</p>
                    <ul className="space-y-2 text-sm text-amber-800">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Review your benchmark period selection - you may need to choose a different time period that better represents pre-event performance</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Verify all financial data is accurate and complete for all years</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Check that the event dates align correctly with your coverage period</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <span>Ensure you've included supporting documentation to validate the claim</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mt-4">
                    <Link 
                      to={`/wizard-v2?resume=${id}`}
                      className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-6 rounded-lg transition"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Report & Recalculate
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BI Results - Using extracted component */}
          {report.result && (
            <BIResultsCard
              result={report.result}
              isPeakSeason={report.is_peak_season}
              contingencyBufferPercent={report.contingency_buffer_percent}
              accountantFees={report.accountant_fees}
              continuingExpenses={report.continuing_expenses}
              extraExpenses={report.extra_expenses}
            />
          )}

          {/* Claim Health Assessment - Traffic Light System */}
          {report.status === 'completed' && (
            <div className="mb-6">
              {healthScoreLoading ? (
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-500">Assessing claim health...</p>
                </div>
              ) : healthScore ? (
                <>
                  <ClaimHealthCard
                    assessment={healthScore}
                    onAction={(cta) => {
                      // Handle different actions
                      if (cta === 'lookup_fema') {
                        setFemaEditing(true);
                      } else if (cta.includes('upload')) {
                        // Navigate back to wizard to upload missing documents
                        navigate(`/wizard-v2?resume=${id}`);
                      }
                    }}
                  />
                  
                  {/* Email Notification Section - Request Missing Documents */}
                  {healthScore.status !== 'GREEN' && (
                    <EmailNotificationSection
                      reportId={id || ''}
                      claimantName={report.claimant_name || report.client_name}
                      claimantEmail={report.client_email}
                      healthStatus={healthScore.status}
                      missingDocsCount={
                        healthScore.critical?.filter((c: any) => c.status === 'FAIL').length || 0
                      }
                    />
                  )}
                  
                  {/* Forensic Analysis - Benford's Law Red Flags (Accountant/Admin only) */}
                  {showAnalytical && healthScore.red_flags && healthScore.red_flags.length > 0 && (
                    <div className="mt-4 bg-white rounded-xl border-2 border-blue-200 p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-3xl">🔍</span>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">Forensic Analysis</h3>
                          <p className="text-sm text-gray-600">AI-powered pattern detection using Benford's Law</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {healthScore.red_flags
                          .filter((flag: any) => flag.category === 'Forensic Analysis' || flag.category?.includes('Pattern'))
                          .map((flag: any, idx: number) => (
                          <div 
                            key={idx}
                            className={`p-4 rounded-lg border-l-4 ${
                              flag.severity === 'HIGH' ? 'bg-red-50 border-red-500' :
                              flag.severity === 'MEDIUM' ? 'bg-amber-50 border-amber-500' :
                              'bg-blue-50 border-blue-500'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                                    flag.severity === 'HIGH' ? 'bg-red-200 text-red-800' :
                                    flag.severity === 'MEDIUM' ? 'bg-amber-200 text-amber-800' :
                                    'bg-blue-200 text-blue-800'
                                  }`}>
                                    {flag.severity}
                                  </span>
                                  <span className="text-xs font-semibold text-gray-500">{flag.category}</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-1">{flag.flag}</h4>
                                <p className="text-sm text-gray-700 mb-2">{flag.detail}</p>
                                {flag.revenue_deviation !== undefined && (
                                  <div className="flex gap-4 text-xs text-gray-600 mb-2">
                                    <span>Revenue Deviation: <strong>{flag.revenue_deviation}%</strong></span>
                                    <span>Expense Deviation: <strong>{flag.expense_deviation}%</strong></span>
                                  </div>
                                )}
                                {flag.recommendation && (
                                  <div className="mt-2 p-2 bg-white/50 rounded text-xs text-gray-600 italic">
                                    💡 {flag.recommendation}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-gray-700">
                        <strong>About Benford's Law:</strong> Natural financial data follows a predictable first-digit distribution. 
                        Deviations &lt;10% are normal, 10-15% warrant review, and &gt;15% may indicate data issues or require additional documentation.
                      </div>
                    </div>
                  )}
                </>
              ) : null}
              
              {/* Policy Upload Button - Prominent placement */}
              {!showPolicyUpload && !report.policy_uploaded && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowPolicyUpload(true)}
                    className="w-full py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-blue-700 transition shadow-lg flex items-center justify-center gap-3"
                  >
                    <span className="text-3xl">🏛️</span>
                    <span>Upload Policy PDF - Auto-Extract Limits & Exclusions</span>
                  </button>
                  <p className="text-center text-sm text-gray-600 mt-2">
                    We'll read your insurance policy and automatically apply coverage limits
                  </p>
                </div>
              )}
              
              {/* Policy Upload Modal */}
              {showPolicyUpload && (
                <div className="mt-4">
                  <PolicyUpload
                    reportId={id}
                    onPolicyUploaded={(data) => {
                      setPolicyData(data);
                      setShowPolicyUpload(false);
                      // Refresh to show coverage summary
                      window.location.reload();
                    }}
                    onClose={() => setShowPolicyUpload(false)}
                  />
                </div>
              )}
              
              {/* Policy Coverage Summary (if uploaded) */}
              {report.policy_uploaded && report.policy_data && (
                <div className="mt-4 bg-white rounded-xl border-2 border-indigo-200 p-6 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">🏛️</span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Policy Coverage Summary</h3>
                      <p className="text-sm text-gray-600">
                        {report.policy_data.policy_info?.carrier || 'Insurance Policy'} • 
                        Confidence: {report.policy_data.confidence || 0}%
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Limits */}
                    {report.policy_data.limits?.aggregate_limit && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <span>💰</span> Coverage Limits
                        </h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-700">Aggregate:</span>
                            <span className="font-bold text-gray-900">
                              {formatCurrency(report.policy_data.limits.aggregate_limit)}
                            </span>
                          </div>
                          {report.policy_data.limits.per_occurrence_limit && (
                            <div className="flex justify-between">
                              <span className="text-gray-700">Per Occurrence:</span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(report.policy_data.limits.per_occurrence_limit)}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* NEW: Claim vs Policy Limit Comparison */}
                        {report.result?.final_claim_amount && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-700 text-sm">Your Claim:</span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(Number(report.result.final_claim_amount))}
                              </span>
                            </div>
                            {(() => {
                              const claim = Number(report.result.final_claim_amount);
                              const limit = report.policy_data.limits.aggregate_limit;
                              const percentage = (claim / limit) * 100;
                              const isOverLimit = claim > limit;
                              const isNearLimit = percentage >= 80 && !isOverLimit;
                              
                              return (
                                <div className={`mt-2 p-2 rounded-lg text-xs ${
                                  isOverLimit ? 'bg-red-100 text-red-800' :
                                  isNearLimit ? 'bg-amber-100 text-amber-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {isOverLimit ? (
                                    <span>⚠️ <strong>Exceeds Policy Limit</strong> by {formatCurrency((claim - limit))} ({percentage.toFixed(1)}% of limit)</span>
                                  ) : isNearLimit ? (
                                    <span>⚡ <strong>Near Policy Limit</strong> - Using {percentage.toFixed(1)}% of coverage</span>
                                  ) : (
                                    <span>✅ <strong>Within Coverage</strong> - Using {percentage.toFixed(1)}% of limit</span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Exclusions */}
                    {report.policy_data.exclusions && report.policy_data.exclusions.length > 0 && (
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <span>🚫</span> Policy Exclusions
                        </h4>
                        <div className="space-y-1">
                          {report.policy_data.exclusions.slice(0, 3).map((exclusion: any, idx: number) => (
                            <div key={idx} className="text-xs">
                              <span className={`font-bold ${
                                exclusion.severity === 'HIGH' ? 'text-red-700' : 'text-gray-700'
                              }`}>
                                {exclusion.type}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* FEMA Declaration Number - Auto-Suggest */}
              {!report.fema_declaration_number && femaSuggestions.length > 0 && !femaEditing && (
                <div className="mt-4 bg-green-50 border-2 border-green-300 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🎯</span>
                      <div>
                        <h4 className="font-bold text-green-800">FEMA Declaration Found!</h4>
                        <p className="text-sm text-green-700 mt-1">
                          We found a matching disaster declaration for your claim:
                        </p>
                        <div className="mt-2 bg-white rounded-lg p-3 border border-green-200">
                          <div className="font-mono font-bold text-lg text-green-800">
                            {femaSuggestions[0].disaster_number}
                          </div>
                          <div className="text-sm text-gray-700 mt-1">
                            {femaSuggestions[0].title}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {femaSuggestions[0].incident_type} • {femaSuggestions[0].begin_date_display} - {femaSuggestions[0].end_date_display}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={async () => {
                          setFemaNumber(femaSuggestions[0].disaster_number);
                          setFemaSaving(true);
                          try {
                            const token = await getToken();
                            await axios.patch(
                              `${BACKEND_URL}/api/v1/reports/${id}`,
                              { fema_declaration_number: femaSuggestions[0].disaster_number },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            // Refresh report and health score
                            const [reportRes, hsRes] = await Promise.all([
                              axios.get(`${BACKEND_URL}/api/v1/reports/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                              axios.get(`${BACKEND_URL}/api/v1/claims/health-score/${id}`, { headers: { Authorization: `Bearer ${token}` } })
                            ]);
                            setReport(reportRes.data);
                            setHealthScore(hsRes.data);
                            setFemaSuggestions([]);
                          } catch (err) {
                            console.error('Failed to apply FEMA:', err);
                          } finally {
                            setFemaSaving(false);
                          }
                        }}
                        disabled={femaSaving}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                      >
                        {femaSaving ? 'Applying...' : '✓ Apply This'}
                      </button>
                      <button
                        onClick={() => setFemaEditing(true)}
                        className="text-sm text-green-700 hover:text-green-900"
                      >
                        Choose different →
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* FEMA Already Set - Show badge */}
              {report.fema_declaration_number && !femaEditing && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">🏛️</span>
                      <div>
                        <h4 className="font-semibold text-blue-900">FEMA Disaster Declaration</h4>
                        <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mt-1">
                          ✓ {report.fema_declaration_number}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setFemaEditing(true)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )}
              
              {/* FEMA Not Found or No State - Show state selector */}
              {!report.fema_declaration_number && femaSuggestions.length === 0 && !femaLoading && !femaEditing && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">🏛️</span>
                    <div>
                      <h4 className="font-semibold text-blue-900">FEMA Disaster Declaration</h4>
                      {femaState ? (
                        <p className="text-sm text-amber-700">
                          ⚠️ No FEMA-declared disasters found in <strong>{femaState}</strong> for {report.event_type || 'this event type'} during {report.event_start_date} - {report.event_end_date || 'present'}.
                          <br/>
                          <span className="text-gray-600">This may mean the event wasn't federally declared, or try selecting a different state.</span>
                        </p>
                      ) : (
                        <p className="text-sm text-blue-700">Select a state to search for matching declarations (+3 health score points)</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <select
                      value={femaState}
                      onChange={(e) => {
                        setFemaState(e.target.value);
                        if (e.target.value) {
                          fetchFemaSuggestionsForState(e.target.value);
                        }
                      }}
                      className="px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select State...</option>
                      {US_STATES.map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                    
                    <span className="text-gray-400">or</span>
                    
                    <button
                      onClick={() => setFemaEditing(true)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Enter FEMA # manually
                    </button>
                  </div>
                  
                  <p className="mt-3 text-xs text-gray-500">
                    Not all events qualify for FEMA declarations. You can still enter a declaration number manually if you have one.
                  </p>
                </div>
              )}
              
              {/* FEMA Loading */}
              {femaLoading && !femaEditing && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-blue-700">Searching FEMA database for matching declarations...</span>
                  </div>
                </div>
              )}
              
              {/* FEMA Edit Mode */}
              {femaEditing && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">🏛️</span>
                    <h4 className="font-semibold text-blue-900">FEMA Disaster Declaration</h4>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Show suggestions if available */}
                    {femaSuggestions.length > 0 && (
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <p className="text-sm font-medium text-blue-800 mb-2">Select a declaration:</p>
                        <div className="space-y-2">
                          {femaSuggestions.slice(0, 3).map((suggestion, idx) => (
                            <button
                              key={idx}
                              onClick={() => setFemaNumber(suggestion.disaster_number)}
                              className={`w-full text-left p-2 rounded-lg border transition ${
                                femaNumber === suggestion.disaster_number 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="font-mono font-bold text-blue-700">{suggestion.disaster_number}</span>
                                  <span className="ml-2 text-sm text-gray-600">{suggestion.title}</span>
                                </div>
                                {idx === 0 && suggestion.match_score >= 50 && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Best Match</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {suggestion.incident_type} • {suggestion.begin_date_display} - {suggestion.end_date_display}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Manual input */}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={femaNumber}
                        onChange={(e) => setFemaNumber(e.target.value.toUpperCase())}
                        placeholder="e.g., DR-4827-NC"
                        className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                      />
                      <button
                        onClick={saveFemaNumber}
                        disabled={femaSaving || !femaNumber.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {femaSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setFemaEditing(false);
                          setFemaNumber(report.fema_declaration_number || '');
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                    
                    <p className="text-xs text-blue-600">
                      Find disasters at <a href="https://www.fema.gov/disaster/declarations" target="_blank" rel="noopener noreferrer" className="underline">fema.gov/disaster/declarations</a>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {report.status === 'processing' || report.status === 'pending' ? (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <div>
                  <p className="font-semibold text-blue-800">PDF generating...</p>
                  <p className="text-sm text-blue-600">Average generation time: 8-15 seconds</p>
                </div>
              </div>
            </div>
          ) : report.status === 'failed' ? (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="font-semibold text-red-800">Report generation failed</p>
              <p className="text-sm text-red-600">Please contact support or try regenerating the report.</p>
            </div>
          ) : report.status === 'completed' ? (
            <div className="space-y-6">
              
              {/* Auto-analysis running indicator */}
              {autoAnalysisRunning && (
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="font-semibold text-blue-800">Running statistical analyses...</p>
                      <p className="text-sm text-blue-600">This will only take a few seconds.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* COMPARATIVE ANALYSIS SECTION (Accountant/Admin only) */}
              {showAnalytical && report.result?.final_claim_amount && report.event_type && (
                <ComparativeAnalysis
                  reportId={id || ''}
                  clientLoss={report.result.final_claim_amount}
                  eventType={report.event_type}
                />
              )}

              {/* COLLABORATION PANEL - Feature #5 */}
              {report.result && (
                <CollaborationPanel reportId={id || ''} />
              )}

              {/* AI ACTIVITY TRAIL */}
              {report.result && (
                <AIActivityPanel reportId={id || ''} />
              )}

              {/* SUBROGATION ANALYSIS SECTION (Accountant/Admin only) */}
              {showAnalytical && report.result && (
                <SubrogationPanel reportId={id || ''} />
              )}

              {/* FUTURE FORCED CLOSURE CALCULATOR (Accountant/Admin only) */}
              {showAnalytical && (report.result || report.calculation_mode === 'future_closure') && (
                <FutureClosurePanel 
                  reportId={id || ''} 
                  reportData={report}
                />
              )}

              {/* LOSS PROJECTION SCENARIOS (Accountant/Admin only) */}
              {showAnalytical && report.result && (
                <ScenarioProjections
                  reportId={id || ''}
                  baseResult={report.result}
                />
              )}

              {/* SENSITIVITY ANALYSIS SECTION (Accountant/Admin only) */}
              {showAnalytical && (
                <SensitivityAnalysisCard
                  data={report.sensitivity_analysis}
                  sensitivityParam={sensitivityParam}
                  sensitivityLoading={sensitivityLoading}
                  onParamChange={setSensitivityParam}
                  onRunAnalysis={runSensitivityAnalysis}
                />
              )}

              {/* MONTE CARLO SIMULATION (Accountant/Admin only) */}
              {showAnalytical && (
                <MonteCarloCard
                  data={report.monte_carlo_analysis}
                  monteCarloLoading={monteCarloLoading}
                  onRunAnalysis={runMonteCarloAnalysis}
                />
              )}

              {/* FAIR SETTLEMENT CALCULATOR (Accountant/Admin only) */}
              {showAnalytical && report.result && (
                <FairSettlementSection
                  reportId={id || ''}
                  getAuthHeader={() => ({ Authorization: `Bearer ${getToken()}` })}
                  formatCurrency={formatCurrency}
                />
              )}

              {/* PDF Download Options - Using extracted component */}
              <DownloadOptionsCard
                reportId={id || ''}
                hasNarrative={!!report.narrative}
                backendUrl={BACKEND_URL || ''}
                onShareClick={() => setShowShareModal(true)}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Adjuster Q&A Bot - Floating */}
      {report && (
        <AdjusterQABot reportId={id || ''} reportData={report} />
      )}
      
      {/* Case Intelligence Chat */}
      {report && (
        <>
          {!showIntelligenceChat && (
            <IntelligenceChatButton onClick={() => setShowIntelligenceChat(true)} />
          )}
          <CaseIntelligenceChat
            reportId={id || ''}
            reportName={report.client_name || report.claimant_name || 'Case'}
            isOpen={showIntelligenceChat}
            onClose={() => setShowIntelligenceChat(false)}
          />
        </>
      )}
      
      {/* Share Report Modal */}
      {report && (
        <ShareReportModal
          reportId={id || ''}
          reportName={report.client_name || 'Report'}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </AppLayout>
  );
}
